import {useEffect, useState} from "react";
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, Tooltip, Typography} from "@mui/material";
import {InfoRounded,} from "@mui/icons-material";
import {Link} from "react-router-dom";
import UploadInput from "../home/upload/uploadInput";
import ShareFileModal from "../home/upload/shareFileModal";
import {uploadFile} from "../../lib/services/drive";
import UploadingFiles from "../home/upload/uploadingFiles";

const UploadModal = () => {

    // Initial Props
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [uploadedFiles, setUploadedFiles] = useState([]);
    const [isUploading, setIsUploading] = useState(false);
    const [isUploadStarted, setIsUploadStarted] = useState(false);
    const [uploadDone, setUploadDone] = useState(false);
    const [generatedLink, setGeneratedLink] = useState(undefined);

    const [uploadTypeDialog, setUploadTypeDialog] = useState(false);
    const [settingModalOpen, setSettingModalOpen] = useState(false);
    const [uploadType, setUploadType] = useState();

    // functions
    const handleToggleModal = () => {
        return setSettingModalOpen(!settingModalOpen);
    }
    const handleFileSelect = (files) => {
        if (files?.length) {
            setUploadTypeDialog(true);
            setSelectedFiles([...files]);
        }
    }
    const handleSelectUploadType = (type) => {
        if (type === uploadTypeDialog) return;
        setUploadType(type);
    }
    const handleContinue = () => {
        switch (uploadType) {
            case "share": {
                setUploadTypeDialog(false);
                setSettingModalOpen(true);
                return;
            }
            case "drive": {
                setUploadTypeDialog(false);
                return;
            }
            default: return;
        }
    }

    const handleSetProgress = (payload, file, progress) => {
        const newSelectedFiles = [...payload];
        const fileIndex = newSelectedFiles.indexOf(file);
        if (fileIndex < 0) return;
        newSelectedFiles[fileIndex].progress = progress;
        setSelectedFiles(newSelectedFiles);
    }
    const handleAbort = (file) => {
        if (file.isCompleted) return;
        const newFiles = [...selectedFiles];
        const index = newFiles.indexOf(file);
        if (index < 0) return;
        const selectedFile = newFiles[index];
        selectedFile.controller.abort();
        selectedFile.isAborted = true;
        setSelectedFiles(newFiles);
    }
    const handleAbortAll = async (options) =>  {
        const {cleanup} = options;
        const newFiles = [...selectedFiles];
        for await (const file of newFiles) {
            if (!file.isCompleted) {
                await file.controller.abort();
                file.isAborted = true;
            }
        }
        if (!cleanup) setSelectedFiles(newFiles);
        else setSelectedFiles([]);
    }
    const handleUploadFiles = async (folderId, linkAddress) => {
        setSettingModalOpen(false);
        setIsUploading(true);
        setIsUploadStarted(true);
        setUploadDone(false);
        const uploadingFiles = [...selectedFiles];
        uploadingFiles.forEach(file => {
            file.controller = new AbortController();
            file.isCompleted = false;
            file.progress = 0;
        });
        setSelectedFiles(uploadingFiles);
        // This "for" method will upload files one by one
        // if you want multiple uploads at the same time use forEach method.
        for await (const file of uploadingFiles) {
            const response = await uploadFile(file, (p) => handleSetProgress(uploadingFiles, file, p), folderId);
            const data = response?.data;
            const fileIndex = uploadingFiles.indexOf(file);
            if (data && data.isSuccess) {
                if (fileIndex >= 0) {
                    uploadingFiles[fileIndex].isCompleted = true;
                    setSelectedFiles(uploadingFiles);
                }
                setUploadedFiles([...uploadedFiles, file]);
            }
            if (data && data.errors?.length) {
                if (fileIndex >= 0) {
                    uploadingFiles[fileIndex].isAborted = true;
                    uploadingFiles[fileIndex].errors = data.errors;
                    uploadingFiles[fileIndex].isCompleted = false;
                }
            }
        }
        setSelectedFiles(uploadingFiles);
        setIsUploading(false);
        setGeneratedLink(linkAddress)
    }

    useEffect(() => {
        if (selectedFiles?.length) {
            const unCompletedFiles = selectedFiles.filter(f => f.isCompleted === false);
            // All Files Uploaded
            if (!unCompletedFiles?.length) {
                setUploadDone(true);
            }
        }
    }, [selectedFiles])

    const optionStyles = {
        width:"49%",
        height: 1,
        overflow: "hidden",
        "& .MuiIconWrapper-root": {
            p: {xs: 1, lg: 2.5},
            mt: 1.5,
            minHeight: {xs: "50vw", md: "35vw", lg: "20vw"},
            borderRadius: 1.5,
            borderWidth: 1,
            borderStyle: "solid",
            borderColor: theme => theme.palette.grey.light,
            filter: "grayscale(1)",
            transition: "filter .35s ease-in",
            cursor: "pointer",
            userSelect: "none"
        },
        "& .MuiIconWrapper-root:hover, &.Mui-active .MuiIconWrapper-root": {
            filter: "none",
            borderColor: theme => theme.palette.primary.main,
            "& > img": {
                transform: "scale(1.05)",
            },
        },
        "&.Mui-active .MuiIconWrapper-root": {
            borderWidth: 2
        },
    }

    return (
        <>
            <Grid container spacing={2}>
                {/* Upload Input */}
                <Grid item xs>
                    <UploadInput onChange={handleFileSelect} disabled={isUploading}/>
                </Grid>
                {/* Uploading Items */}
                {isUploadStarted && selectedFiles.length ?
                    <Grid item xs={12} lg={4}>
                        <UploadingFiles
                            files={selectedFiles}
                            setFiles={setSelectedFiles}
                            onAbort={handleAbort}
                            onAbortAll={handleAbortAll}
                            isUploading={isUploading}
                            uploadDone={uploadDone}
                            generatedLink={generatedLink}/>
                    </Grid>
                : null}
            </Grid>
            {/* Upload Method Select Dialog */}
            <Dialog open={uploadTypeDialog} onClose={() => setUploadTypeDialog(false)} PaperProps={{ sx: { m: 0, width: {xs: "90%", lg: "auto" }, minWidth: { lg: "50%" }, maxWidth: {xs: "90%", lg: "85%", xl: "75%", xxl: "65%"}, minHeight: "50%", borderRadius: 2.5 } }}>
                <DialogTitle>انتخاب روش آپلود</DialogTitle>
                <DialogContent sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <Box sx={optionStyles} className={uploadType === "share" ? 'Mui-active' : ''}>
                        <Box display="flex" alignItems="center" justifyContent="center">
                            <Typography mr={1} fontSize={{ xs: ".925rem", lg: "1rem" }} fontWeight={600} textAlign="center">ارسال برای دیگران</Typography>
                            <Tooltip title="توضیحات">
                                <InfoRounded color="info"/>
                            </Tooltip>
                        </Box>
                        <Box onClick={() => handleSelectUploadType("share")}  className="MuiIconWrapper-root">
                            <Box component="img" alt="Icon" src="/assets/image/icon/share.svg" width={1} height={1} sx={{ objectFit: "contain", transition: "transform .3s ease" }}/>
                        </Box>
                    </Box>
                    <Box sx={optionStyles} className={uploadType === "drive" ? 'Mui-active' : ''}>
                        <Box display="flex" alignItems="center" justifyContent="center">
                            <Typography mr={1} fontSize={{ xs: ".925rem", lg: "1rem" }} fontWeight={600} textAlign="center">آپلود در درایو</Typography>
                            <Tooltip title="توضیحات">
                                <InfoRounded color="info"/>
                            </Tooltip>
                        </Box>
                        <Link to="/drive">
                            <Box onClick={() => {handleSelectUploadType("drive"); }} className="MuiIconWrapper-root">
                                <Box component="img" alt="Icon" src="/assets/image/icon/upload-drive.svg" width={1} height={1} sx={{ objectFit: "contain", transition: "transform .3s ease" }}/>
                            </Box>
                        </Link>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ p: 2.5, flexDirection: {xs: "column", lg: "row"} }}>
                    <Button onClick={handleContinue} fullWidth variant="contained" color="success" disableElevation sx={{ py: 1.5 }}>ادامه</Button>
                    <Button onClick={() => setUploadTypeDialog(false)} fullWidth variant="contained" color="error" sx={{ mt: {xs: 1, lg: 0}, ml: {xs: "0!important", lg: "8px!important"}, py: 1.4 }} disableElevation>انصراف</Button>
                </DialogActions>
            </Dialog>
            {/* Share Settings */}
            <ShareFileModal
                open={settingModalOpen}
                onClose={handleToggleModal}
                shareType="newFolder"
                files={selectedFiles}
                setFiles={setSelectedFiles}
                onConfirm={handleUploadFiles}/>
        </>
    )
}
export default UploadModal;